import axios from 'axios';
import config from '../config';

// Get the image processing URL based on environment
const getImageProcessingUrl = () => {
  if (config.environment === 'production') {
    // In production, use the domain without port
    return 'https://imageserver.geteditify.com';
  } else {
    // In development, use the dynamic URL with port
    return config.getServerUrl('imageProcessing');
  }
};

const IMAGE_PROCESSING_URL = getImageProcessingUrl();

class ImageProcessingService {
  static async processImage(imageUrl, options, shopId, shopifyMetadata = null) {
    try {
      // Check if this image has already been processed recently
      if (shopifyMetadata?.id) {
        const savedState = await this.loadProcessedImageState(shopId);
        const existingImage = savedState?.processedImages?.find(
          item => item.originalImage?.id === shopifyMetadata.id
        );
        
        if (existingImage && existingImage.processedImage) {
          console.log('Image already processed, returning cached result:', shopifyMetadata.id);
          return existingImage.processedImage;
        }
      }

      // Validate inputs
      if (!imageUrl) {
        throw new Error('Image URL is required');
      }

      if (!shopId) {
        throw new Error('Shop ID is required');
      }

      // Validate image URL format
      try {
        new URL(imageUrl);
      } catch (e) {
        throw new Error('Invalid image URL format');
      }

      // Get original file size
      let originalSize = null;
      try {
        const response = await axios.head(imageUrl);
        originalSize = parseInt(response.headers['content-length'], 10);
      } catch (error) {
        console.warn('Could not get original file size:', error);
      }

      // Add validation for image URL accessibility with retries
      let accessibilityError = null;
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          const response = await axios.head(imageUrl, {
            timeout: 10000,
            validateStatus: status => status === 200
          });
          
          if (!response.headers['content-type']?.startsWith('image/')) {
            throw new Error('URL does not point to a valid image');
          }
          
          accessibilityError = null;
          break;
        } catch (error) {
          accessibilityError = error;
          if (attempt < 3) {
            await new Promise(resolve => setTimeout(resolve, 1000 * attempt));
          }
        }
      }

      if (accessibilityError) {
        throw new Error('Image URL is not accessible after 3 attempts: ' + accessibilityError.message);
      }

      // Generate SEO-friendly filename if enabled
      let filename = '';
      if (options.useSeoNaming && options.productTitle) {
        // Extract current image number from URL if it exists
        const currentUrl = new URL(imageUrl);
        const pathParts = currentUrl.pathname.split('/');
        const currentFilename = pathParts[pathParts.length - 1];
        const numberMatch = currentFilename.match(/_(\d+)\./);
        const imageNumber = numberMatch ? parseInt(numberMatch[1]) : 0;
        
        // Generate SEO filename
        filename = options.getSeoFilename(options.productTitle, imageNumber);
      }

      // Add CORS headers
      const axiosConfig = {
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        timeout: 30000,
        withCredentials: false,
        validateStatus: function (status) {
          return status >= 200 && status < 500;
        }
      };

      // Make the request with timeout and retries
      let processingError = null;
      let jobId = null;

      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          console.log(`Attempt ${attempt}: Sending request to ${IMAGE_PROCESSING_URL}/process`, {
            config: {
              url: `${IMAGE_PROCESSING_URL}/process`,
              headers: axiosConfig.headers
            }
          });
          
          const response = await axios.post(`${IMAGE_PROCESSING_URL}/process`, {
            imageUrl,
            options: {
              ...options,
              filename: filename || options.filename || ''
            },
            shopId,
            shopifyMetadata
          }, axiosConfig);

          if (!response.data || !response.data.jobId) {
            throw new Error('Invalid response from image processing server');
          }

          jobId = response.data.jobId;
          console.log(`Job ID received: ${jobId}`);
          
          // Wait for the job to complete
          const result = await this.waitForProcessing(jobId);
          
          // Validate and structure the processed data
          if (!result) {
            throw new Error('No result received from image processing server');
          }

          if (!result.processedUrl) {
            throw new Error('No processed URL received from image processing server');
          }

          if (!result.metadata) {
            throw new Error('No metadata received for processed image');
          }

          // Clean up the processedUrl if needed
          const processedUrl = result.processedUrl?.startsWith('/home')
            ? result.processedUrl.replace('/home/plesk/geteditify.com/imageserver.geteditify.com', '')
            : result.processedUrl;

          // Structure the response data
          const processedData = {
            jobId,
            processedUrl,
            filename: result.filename || filename || '',
            metadata: {
              format: result.metadata?.format || options.format || 'webp',
              width: parseInt(result.metadata?.width) || 0,
              height: parseInt(result.metadata?.height) || 0,
              size: parseInt(result.metadata?.size) || 0,
              options: {
                ...options,
                filename: filename || options.filename || ''
              },
              shopifyMetadata
            },
            originalSize: parseInt(originalSize) || 0,
            processedSize: parseInt(result.metadata?.size) || 0,
            bytesSaved: originalSize && result.metadata?.size ? 
              parseInt(originalSize) - parseInt(result.metadata.size) : 0,
            compressionRatio: originalSize && result.metadata?.size ? 
              ((parseInt(originalSize) - parseInt(result.metadata.size)) / parseInt(originalSize) * 100).toFixed(2) : 0
          };

          console.log('Processed Image Data:', {
            originalSize: processedData.originalSize,
            processedSize: processedData.processedSize,
            bytesSaved: processedData.bytesSaved
          });

          // Save the state immediately after processing
          if (shopifyMetadata?.id) {
            try {
              const savedState = await this.loadProcessedImageState(shopId) || { processedImages: [] };
              
              // Remove any existing entries for this image
              savedState.processedImages = savedState.processedImages.filter(
                item => item.originalImage?.id !== shopifyMetadata.id
              );
              
              // Add the new processed image data
              savedState.processedImages.push({
                originalImage: shopifyMetadata,
                processedImage: processedData,
                action: 'update'
              });

              await this.saveProcessedImageState(shopId, savedState);
            } catch (error) {
              console.error('Error saving processed image state:', error);
              // Continue with the return even if save fails
            }
          }

          return processedData;
        } catch (error) {
          processingError = error;
          console.error(`Processing attempt ${attempt} failed:`, {
            message: error.message,
            code: error.code,
            response: error.response?.data,
            status: error.response?.status
          });
          
          // Don't retry if it's a validation error
          if (error.response?.status === 400) {
            throw error;
          }
          
          if (attempt < 3) {
            await new Promise(resolve => setTimeout(resolve, 2000 * attempt));
            continue;
          }
          
          throw error;
        }
      }

      throw processingError || new Error('Failed to process image after all retries');
    } catch (error) {
      console.error('Error in processImage:', error);
      throw error;
    }
  }

  static async getJobStatus(jobId) {
    try {
      if (!jobId) {
        throw new Error('Job ID is required');
      }

      const response = await axios.get(`${IMAGE_PROCESSING_URL}/status/${jobId}`);
      
      if (!response.data || !response.data.status) {
        throw new Error('Invalid status response from server');
      }

      return response.data;
    } catch (error) {
      console.error('Error getting job status:', error);
      if (error.response && error.response.status === 404) {
        throw new Error('Job not found');
      }
      throw error;
    }
  }

  static async waitForProcessing(jobId, onProgress, maxAttempts = 30, delayMs = 1000) {
    let attempts = 0;
    let lastError = null;
    let consecutiveErrors = 0;

    const checkStatus = async () => {
      try {
        const status = await this.getJobStatus(jobId);
        consecutiveErrors = 0; // Reset consecutive errors on success
        
        if (onProgress) {
          onProgress(status);
        }

        if (status.status === 'completed') {
          // Get metadata for the processed file
          let processedSize = null;
          let metadata = {};
          try {
            const metadataUrl = `${IMAGE_PROCESSING_URL}/metadata${status.processedUrl}`;
            const response = await axios.get(metadataUrl);
            metadata = response.data;
            processedSize = response.data.size;
          } catch (error) {
            console.warn('Could not get processed file metadata:', error);
          }

          return {
            jobId,
            processedUrl: status.processedUrl,
            filename: status.filename,
            metadata: {
              format: metadata.format || status.metadata?.format || 'webp',
              width: metadata.width || status.metadata?.width || 0,
              height: metadata.height || status.metadata?.height || 0,
              size: processedSize || status.metadata?.size || 0
            }
          };
        }
        
        if (status.status === 'failed') {
          throw new Error(status.error || 'Image processing failed');
        }
        
        // Check if we've exceeded max attempts
        attempts++;
        if (attempts >= maxAttempts) {
          throw new Error('Processing timeout exceeded');
        }

        // Wait before checking again
        await new Promise(resolve => setTimeout(resolve, delayMs));
        return checkStatus();
      } catch (error) {
        console.error('Error in waitForProcessing:', error);
        
        // Handle consecutive errors
        consecutiveErrors++;
        if (consecutiveErrors >= 3) {
          throw new Error('Too many consecutive errors while checking status');
        }

        // Exponential backoff for retries
        await new Promise(resolve => setTimeout(resolve, delayMs * Math.pow(2, consecutiveErrors)));
        return checkStatus();
      }
    };

    return checkStatus();
  }

  static formatFileSize(bytes) {
    if (!bytes || isNaN(bytes)) return '0 B';
    
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${sizes[i]}`;
  }

  static async getImageFileSize(imageUrl) {
    try {
      // For processed images on our server
      if (imageUrl.includes('imageserver.geteditify.com')) {
        // Instead of checking size, just return success for now
        // We'll implement proper size checking later
        return 1;
      } else {
        // For external images (like Shopify CDN)
        const response = await fetch(imageUrl, {
          method: 'HEAD'
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const contentLength = response.headers.get('content-length');
        return contentLength ? parseInt(contentLength, 10) : null;
      }
    } catch (error) {
      console.error('Error checking file size:', error);
      return null;
    }
  }

  static async getImageFileSizeAndDimensions(imageUrl) {
    try {
      // Use fetch instead of axios for HEAD request
      const response = await fetch(imageUrl, {
        method: 'HEAD',
        credentials: 'include',
        headers: {
          'Accept': '*/*'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const contentLength = response.headers.get('content-length');
      const size = contentLength ? parseInt(contentLength, 10) : null;

      // Get dimensions by loading the image
      const img = new Image();
      const dimensionsPromise = new Promise((resolve, reject) => {
        img.onload = () => {
          resolve({
            width: img.width,
            height: img.height
          });
        };
        img.onerror = () => {
          reject(new Error('Failed to load image'));
        };
      });

      img.src = imageUrl;
      const dimensions = await dimensionsPromise;

      return {
        size,
        formattedSize: this.formatFileSize(size),
        ...dimensions
      };
    } catch (error) {
      console.error('Error getting image size and dimensions:', error);
      return {
        size: 0,
        formattedSize: '0 B',
        width: 0,
        height: 0
      };
    }
  }

  static async getImageMetadata(filePath, shopId) {
    try {
      // Normalize the path to use forward slashes
      const normalizedPath = filePath.replace(/\\/g, '/');
      
      // Ensure path starts with /temp/
      const metadataPath = normalizedPath.startsWith('/temp/') 
        ? normalizedPath 
        : `/temp/${normalizedPath}`;

      // Remove any double slashes
      const cleanPath = metadataPath.replace(/\/+/g, '/');
      
      const response = await axios.get(`${IMAGE_PROCESSING_URL}/metadata${cleanPath}`, {
        headers: {
          'Accept': 'application/json',
          'X-Shop-Id': shopId
        },
        timeout: 10000,
        validateStatus: status => status === 200
      });

      return response.data;
    } catch (error) {
      console.error('Error getting image metadata:', error);
      throw new Error(`Failed to get image metadata: ${error.message}`);
    }
  }

  static async cleanupProcessedImages(shopId, jobIds) {
    try {
      if (!shopId || !jobIds || !Array.isArray(jobIds)) {
        throw new Error('Shop ID and array of job IDs are required');
      }

      const response = await axios.post(`${IMAGE_PROCESSING_URL}/cleanup`, {
        shopId,
        jobIds
      }, {
        timeout: 30000,
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return response.data;
    } catch (error) {
      console.error('Error cleaning up processed images:', error);
      throw error;
    }
  }

  static async saveProcessedImageState(shopId, state) {
    try {
      const response = await axios.post(`${IMAGE_PROCESSING_URL}/state/${shopId}`, state, {
        timeout: 30000,
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return response.data;
    } catch (error) {
      console.error('Error saving processed image state:', error);
      throw error;
    }
  }

  static async loadProcessedImageState(shopId) {
    try {
      const timestamp = new Date().getTime();
      const response = await axios.get(`${IMAGE_PROCESSING_URL}/state/${shopId}?_t=${timestamp}`, {
        timeout: 30000,
        headers: {
          'Content-Type': 'application/json',
        },
      });

      return response.data;
    } catch (error) {
      console.error('Error loading processed image state:', error);
      return null; // Return null if state doesn't exist
    }
  }

  static async clearProcessedImageState(shopId) {
    try {
      if (!shopId) {
        throw new Error('Shop ID is required');
      }

      const response = await axios.delete(`${IMAGE_PROCESSING_URL}/state/${shopId}`, {
        timeout: 30000,
        headers: {
          'Content-Type': 'application/json'
        }
      });

      return response.data;
    } catch (error) {
      console.error('Error clearing processed image state:', error);
      throw error;
    }
  }
}

export default ImageProcessingService;
